import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TimeRange } from "@/core/components/ui/time-range-picker/time-range-picker";
import { USER_REPORTING_PRESETS } from "@/business-settings/common/user-reporting-presets";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import {
  useGetWebAppUsers,
  useGetUserStatsByBusinessWithTimeRange,
} from "@/user/query";
import { DetailedUser, DetailedUserWithStats } from "@/user/user";
import { UsersTable } from "./users-table";

export const WebAppUsers = () => {
  const { businessId } = useParams();
  const [showReporting, setShowReporting] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  const {
    state: { selectedItemId },
    updateSidebar,
  } = useBusinessSettingsSidebar();

  const { data: users = [], isLoading: isLoadingUsers } =
    useGetWebAppUsers(businessId);
  const { data: userStatsByBusiness, isLoading: isLoadingStats } =
    useGetUserStatsByBusinessWithTimeRange(
      businessId,
      showReporting ? timeRange : undefined,
    );

  const isLoading = isLoadingUsers || (showReporting && isLoadingStats);

  const usersWithStats = useMemo(
    () =>
      showReporting
        ? users.map((user) => ({
            ...user,
            stats: userStatsByBusiness?.[user.id]?.stats,
          }))
        : users,
    [users, userStatsByBusiness, showReporting],
  );

  const handleSelectUser = (user: DetailedUser | DetailedUserWithStats) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.users,
      mode: SIDEBAR_MODES.view,
      selectedItemId: user.id,
      isOpen: true,
    });
  };

  return (
    <UsersTable
      data={usersWithStats}
      isLoading={isLoading}
      isLoadingStats={isLoadingStats}
      onRowClick={handleSelectUser}
      selectedUserId={selectedItemId}
      className="h-full w-full"
      tableClassName="min-w-[900px]"
      showReporting={showReporting}
      setShowReporting={setShowReporting}
      timeRange={timeRange}
      setTimeRange={setTimeRange}
      presets={USER_REPORTING_PRESETS}
    />
  );
};
